import React from "react";
import {isFuture, parseISO} from "date-fns";

import {compact, isEmpty} from "@pg-mono/nodash";
import {areaFormat, dateTimeFormat, formatDate, pluralize, priceFormat, priceM2Format} from "@pg-mono/string-utils";

import {renderParkingPlacesGenericText} from "../../offer/detail/components/OfferDetailGeneratedDescriptionParkingPlaces";
import {IRangeQuery} from "../../request/IRangeQuery";
import {VendorType} from "../../vendor/helpers/VendorType";
import {
    getPropertyHouseTypeVerbose,
    getPropertyHouseTypeWithoutNoun,
    getRoomListTypeMap,
    RoomListType
} from "../helpers/generated_decription/generated_description";
import {IAdditionalArea} from "../interfaces/IAdditionalArea";
import {IParkingPlace} from "../interfaces/IParkingPlace";
import {IRoomAreaLabel} from "../interfaces/IRoomArea";

interface IProps {
    additionalAreaList: IAdditionalArea[];
    showDescriptionPrice: boolean;
    quarterTypeList: string[];
    renderParkingPlaces: string[];
    roomAreaList: IRoomAreaLabel[];
    property: {
        configuration: {
            show_price_m2: boolean;
        };
        garages: number;
        house_type: number;
        has_attic: boolean;
        has_basement: boolean;
        floors: number;
        number: string;
        offer: {
            construction_date_range: IRangeQuery<string>;
            name: string;
            investor: string | null;
            parking_places: IParkingPlace[];
            properties: number;
            stats: {
                ranges_height_max: number;
                ranges_height_min: number;
            } | null;
            vendor: {
                name: string;
                type: VendorType;
            };
        };
        plot_area: number;
        price: number | null;
        price_m2: number;
        rooms: number;
        room_areas: {
            type: number;
            area: number;
        }[];
    };
}

export const PropertyDetailGeneratedDescriptionHouses: React.FunctionComponent<IProps> = (props) => {
    const {property, roomAreaList, additionalAreaList, quarterTypeList, renderParkingPlaces} = props;
    const {offer} = property;
    const upper = formatDate(offer.construction_date_range.upper, dateTimeFormat.quarter);
    const futureEndDate = isFuture(parseISO(offer.construction_date_range.upper));
    const futureStartDate = isFuture(parseISO(offer.construction_date_range.lower));
    const roomsWho = pluralize(["pokój", "pokoje", "pokoi"]);
    const roomVerbose = pluralize(["znajduje się", "znajdują się", "znajduje się"]);
    const roomCount = pluralize(["przynależy", "przynależą", "przynależy"]);

    const houseTypeWithoutNoun = props.property.house_type ? getPropertyHouseTypeWithoutNoun(props.property.house_type) : "";
    const houseTypeVerbose = props.property.house_type ? getPropertyHouseTypeVerbose(props.property.house_type) : "";

    const listQuarterTypesElements = quarterTypeList.map((item) => item);
    const additionalAreaListElement = additionalAreaList.map(
        (item) => `${item.type.toString().toLowerCase()}${item.area ? ` o powierzchni ${areaFormat(item.area, {precision: 2})}` : ""}`
    );
    const roomListTypeMap = React.useMemo(() => getRoomListTypeMap(roomAreaList), [roomAreaList]);
    const basicCollection =
        !isEmpty(roomListTypeMap) &&
        compact([roomListTypeMap[RoomListType.LIVING_ROOM], roomListTypeMap[RoomListType.ROOM], roomListTypeMap[RoomListType.BEDROOM]]);

    const renderBasic = () => {
        return basicCollection && basicCollection.length
            ? basicCollection.map((rooms) => {
                  return (
                      <React.Fragment key={rooms.type}>
                          {rooms.type.toLowerCase()} {rooms.areasString ? ` o powierzchni ${rooms.areasString}` : ""}
                          {", "}
                      </React.Fragment>
                  );
              })
            : null;
    };

    const renderAdditional = () => {
        const additionalCollection =
            !isEmpty(roomListTypeMap) &&
            compact([
                roomListTypeMap[RoomListType.CORRIDOR],
                roomListTypeMap[RoomListType.DINING_ROOM],
                roomListTypeMap[RoomListType.OFFICE],
                roomListTypeMap[RoomListType.WC],
                roomListTypeMap[RoomListType.STAIRS],
                roomListTypeMap[RoomListType.WARDROBE],
                roomListTypeMap[RoomListType.UTILITY_ROOM],
                roomListTypeMap[RoomListType.ENTRESOL]
            ]);

        return additionalCollection
            ? additionalCollection.map((rooms, index) => {
                  return (
                      <React.Fragment key={rooms.type}>
                          {rooms.type.toLowerCase()}
                          {rooms.areasString ? ` o powierzchni ${rooms.areasString}` : ""}
                          {index === additionalCollection.length - 1 ? "." : ", "}
                      </React.Fragment>
                  );
              })
            : null;
    };

    return (
        <>
            <p>Cała inwestycja składa się z {offer.properties} nowych domów.</p>
            <p>
                {futureEndDate || !upper
                    ? `Inwestycja ${offer.name} jest ${futureStartDate ? "w trakcie przygotowań" : "w trakcie budowy"} ${
                          upper && `z planowanym na ${upper} roku terminem oddania do użytkowania`
                      }`
                    : `Inwestycja ${offer.name} została wybudowana i oddana do użytkowania ${upper && `w ${upper} roku`}`}
                .
            </p>
            <p>
                Nowy dom {houseTypeWithoutNoun} o oznaczeniu {property.number} {property.plot_area && `stoi na działce o powierzchni ${property.plot_area} m²`},{" "}
                {property.floors && `składa się z ${property.floors} kondygnacji`} {property.has_attic ? "w tym poddasza" : "bez poddasza"},{" "}
                {property.has_basement ? "posiada podpiwniczenie" : "nie posiada podpiwniczenia"}. Wysokość pomieszczeń wynosi{" "}
                {offer.stats?.ranges_height_min === offer.stats?.ranges_height_max
                    ? offer.stats?.ranges_height_min
                    : `od ${offer.stats?.ranges_height_min} do ${offer.stats?.ranges_height_max}`}{" "}
                cm. {property.garages && `Dom ma garaż ${property.garages} stanowiskowy.`}
            </p>
            <p>
                W domu {houseTypeVerbose} numer {property.number} {roomVerbose(property.rooms)} się {property.rooms} {roomsWho(property.rooms)}
                {basicCollection && basicCollection.length ? ": " : ". "}
                {renderBasic()}
                {!!roomListTypeMap[RoomListType.KITCHEN] && (
                    <>
                        {roomListTypeMap[RoomListType.KITCHEN].type.toLowerCase()}
                        {roomListTypeMap[RoomListType.KITCHEN].areasString ? ` o powierzchni ${roomListTypeMap[RoomListType.KITCHEN].areasString}, ` : ""}
                    </>
                )}
                {!!roomListTypeMap[RoomListType.BATHROOM] && (
                    <>
                        {roomListTypeMap[RoomListType.BATHROOM].rooms.length}{" "}
                        {pluralize(["łazienka", "łazienki", "łazienek"])(roomListTypeMap[RoomListType.BATHROOM].rooms.length)}{" "}
                        {roomListTypeMap[RoomListType.BATHROOM].areasString ? ` o powierzchni ${roomListTypeMap[RoomListType.BATHROOM].areasString}, ` : ""}
                    </>
                )}
                {!isEmpty(property.room_areas) && `W domu ${property.room_areas.length > 1 ? "są" : "jest"} także: `}
                {renderAdditional()}
            </p>

            {!isEmpty(quarterTypeList) && <p>Okna domu wychodzą na {listQuarterTypesElements.join(", ")}.</p>}
            {!isEmpty(additionalAreaList) && (
                <p>
                    Do domu {roomCount(additionalAreaListElement.length)} {additionalAreaListElement.join(", ")}.
                </p>
            )}
            {renderParkingPlacesGenericText(!isEmpty(offer.parking_places), property.offer.name, renderParkingPlaces)}

            {props.showDescriptionPrice ? (
                <p>
                    Cena ofertowa domu wynosi {priceFormat(property.price as number)}
                    {property.price_m2 && property.configuration.show_price_m2 ? ` (${priceM2Format(property.price_m2)})` : ""}.
                </p>
            ) : null}
        </>
    );
};
import React from "react";
import {isFuture, parseISO} from "date-fns";

import {compact, isEmpty} from "@pg-mono/nodash";
import {areaFormat, dateTimeFormat, formatDate, pluralize, priceFormat, priceM2Format} from "@pg-mono/string-utils";

import {renderParkingPlacesGenericText} from "../../offer/detail/components/OfferDetailGeneratedDescriptionParkingPlaces";
import {IRangeQuery} from "../../request/IRangeQuery";
import {VendorType} from "../../vendor/helpers/VendorType";
import {
    getPropertyHouseTypeVerbose,
    getPropertyHouseTypeWithoutNoun,
    getRoomListTypeMap,
    RoomListType
} from "../helpers/generated_decription/generated_description";
import {IAdditionalArea} from "../interfaces/IAdditionalArea";
import {IParkingPlace} from "../interfaces/IParkingPlace";
import {IRoomAreaLabel} from "../interfaces/IRoomArea";

interface IProps {
    additionalAreaList: IAdditionalArea[];
    showDescriptionPrice: boolean;
    quarterTypeList: string[];
    renderParkingPlaces: string[];
    roomAreaList: IRoomAreaLabel[];
    property: {
        configuration: {
            show_price_m2: boolean;
        };
        garages: number;
        house_type: number;
        has_attic: boolean;
        has_basement: boolean;
        floors: number;
        number: string;
        offer: {
            construction_date_range: IRangeQuery<string>;
            name: string;
            investor: string | null;
            parking_places: IParkingPlace[];
            properties: number;
            stats: {
                ranges_height_max: number;
                ranges_height_min: number;
            } | null;
            vendor: {
                name: string;
                type: VendorType;
            };
        };
        plot_area: number;
        price: number | null;
        price_m2: number;
        rooms: number;
        room_areas: {
            type: number;
            area: number;
        }[];
    };
}

export const PropertyDetailGeneratedDescriptionHouses: React.FunctionComponent<IProps> = (props) => {
    const {property, roomAreaList, additionalAreaList, quarterTypeList, renderParkingPlaces} = props;
    const {offer} = property;
    const upper = formatDate(offer.construction_date_range.upper, dateTimeFormat.quarter);
    const futureEndDate = isFuture(parseISO(offer.construction_date_range.upper));
    const futureStartDate = isFuture(parseISO(offer.construction_date_range.lower));
    const roomsWho = pluralize(["pokój", "pokoje", "pokoi"]);
    const roomVerbose = pluralize(["znajduje się", "znajdują się", "znajduje się"]);
    const roomCount = pluralize(["przynależy", "przynależą", "przynależy"]);

    const houseTypeWithoutNoun = props.property.house_type ? getPropertyHouseTypeWithoutNoun(props.property.house_type) : "";
    const houseTypeVerbose = props.property.house_type ? getPropertyHouseTypeVerbose(props.property.house_type) : "";

    const listQuarterTypesElements = quarterTypeList.map((item) => item);
    const additionalAreaListElement = additionalAreaList.map(
        (item) => `${item.type.toString().toLowerCase()}${item.area ? ` o powierzchni ${areaFormat(item.area, {precision: 2})}` : ""}`
    );
    const roomListTypeMap = React.useMemo(() => getRoomListTypeMap(roomAreaList), [roomAreaList]);
    const basicCollection =
        !isEmpty(roomListTypeMap) &&
        compact([roomListTypeMap[RoomListType.LIVING_ROOM], roomListTypeMap[RoomListType.ROOM], roomListTypeMap[RoomListType.BEDROOM]]);

    const renderBasic = () => {
        return basicCollection && basicCollection.length
            ? basicCollection.map((rooms) => {
                  return (
                      <React.Fragment key={rooms.type}>
                          {rooms.type.toLowerCase()} {rooms.areasString ? ` o powierzchni ${rooms.areasString}` : ""}
                          {", "}
                      </React.Fragment>
                  );
              })
            : null;
    };

    const renderAdditional = () => {
        const additionalCollection =
            !isEmpty(roomListTypeMap) &&
            compact([
                roomListTypeMap[RoomListType.CORRIDOR],
                roomListTypeMap[RoomListType.DINING_ROOM],
                roomListTypeMap[RoomListType.OFFICE],
                roomListTypeMap[RoomListType.WC],
                roomListTypeMap[RoomListType.STAIRS],
                roomListTypeMap[RoomListType.WARDROBE],
                roomListTypeMap[RoomListType.UTILITY_ROOM],
                roomListTypeMap[RoomListType.ENTRESOL]
            ]);

        return additionalCollection
            ? additionalCollection.map((rooms, index) => {
                  return (
                      <React.Fragment key={rooms.type}>
                          {rooms.type.toLowerCase()}
                          {rooms.areasString ? ` o powierzchni ${rooms.areasString}` : ""}
                          {index === additionalCollection.length - 1 ? "." : ", "}
                      </React.Fragment>
                  );
              })
            : null;
    };

    return (
        <>
            <p>Cała inwestycja składa się z {offer.properties} nowych domów.</p>
            <p>
                {futureEndDate || !upper
                    ? `Inwestycja ${offer.name} jest ${futureStartDate ? "w trakcie przygotowań" : "w trakcie budowy"} ${
                          upper && `z planowanym na ${upper} roku terminem oddania do użytkowania`
                      }`
                    : `Inwestycja ${offer.name} została wybudowana i oddana do użytkowania ${upper && `w ${upper} roku`}`}
                .
            </p>
            <p>
                Nowy dom {houseTypeWithoutNoun} o oznaczeniu {property.number} {property.plot_area && `stoi na działce o powierzchni ${property.plot_area} m²`},{" "}
                {property.floors && `składa się z ${property.floors} kondygnacji`} {property.has_attic ? "w tym poddasza" : "bez poddasza"},{" "}
                {property.has_basement ? "posiada podpiwniczenie" : "nie posiada podpiwniczenia"}. Wysokość pomieszczeń wynosi{" "}
                {offer.stats?.ranges_height_min === offer.stats?.ranges_height_max
                    ? offer.stats?.ranges_height_min
                    : `od ${offer.stats?.ranges_height_min} do ${offer.stats?.ranges_height_max}`}{" "}
                cm. {property.garages && `Dom ma garaż ${property.garages} stanowiskowy.`}
            </p>
            <p>
                W domu {houseTypeVerbose} numer {property.number} {roomVerbose(property.rooms)} się {property.rooms} {roomsWho(property.rooms)}
                {basicCollection && basicCollection.length ? ": " : ". "}
                {renderBasic()}
                {!!roomListTypeMap[RoomListType.KITCHEN] && (
                    <>
                        {roomListTypeMap[RoomListType.KITCHEN].type.toLowerCase()}
                        {roomListTypeMap[RoomListType.KITCHEN].areasString ? ` o powierzchni ${roomListTypeMap[RoomListType.KITCHEN].areasString}, ` : ""}
                    </>
                )}
                {!!roomListTypeMap[RoomListType.BATHROOM] && (
                    <>
                        {roomListTypeMap[RoomListType.BATHROOM].rooms.length}{" "}
                        {pluralize(["łazienka", "łazienki", "łazienek"])(roomListTypeMap[RoomListType.BATHROOM].rooms.length)}{" "}
                        {roomListTypeMap[RoomListType.BATHROOM].areasString ? ` o powierzchni ${roomListTypeMap[RoomListType.BATHROOM].areasString}, ` : ""}
                    </>
                )}
                {!isEmpty(property.room_areas) && `W domu ${property.room_areas.length > 1 ? "są" : "jest"} także: `}
                {renderAdditional()}
            </p>

            {!isEmpty(quarterTypeList) && <p>Okna domu wychodzą na {listQuarterTypesElements.join(", ")}.</p>}
            {!isEmpty(additionalAreaList) && (
                <p>
                    Do domu {roomCount(additionalAreaListElement.length)} {additionalAreaListElement.join(", ")}.
                </p>
            )}
            {renderParkingPlacesGenericText(!isEmpty(offer.parking_places), property.offer.name, renderParkingPlaces)}

            {props.showDescriptionPrice ? (
                <p>
                    Cena ofertowa domu wynosi {priceFormat(property.price as number)}
                    {property.price_m2 && property.configuration.show_price_m2 ? ` (${priceM2Format(property.price_m2)})` : ""}.
                </p>
            ) : null}
        </>
    );
};
